import { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Activity } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface DiagnosticResult {
  test: string;
  duration: number;
  status: 'success' | 'error';
  details: string;
}

export function PerformanceDiagnostic({ accessToken }: { accessToken: string }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[]>([]);

  const runDiagnostics = async () => {
    setRunning(true);
    const newResults: DiagnosticResult[] = [];

    // Test 1: Health check (no auth)
    try {
      const start = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/health`
      );
      const duration = Date.now() - start;
      const data = await response.json();
      newResults.push({
        test: 'Health Check (No Auth)',
        duration,
        status: response.ok ? 'success' : 'error',
        details: response.ok ? `Server is running. Cache size: ${data.authCacheSize}` : 'Failed'
      });
    } catch (error) {
      newResults.push({
        test: 'Health Check (No Auth)',
        duration: 0,
        status: 'error',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown'}`
      });
    }

    // Test 2: Session check (with auth)
    try {
      const start = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/session`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );
      const duration = Date.now() - start;
      newResults.push({
        test: 'Session Check (First Time)',
        duration,
        status: response.ok ? 'success' : 'error',
        details: response.ok ? `Auth validated in ${duration}ms` : `Failed: ${response.status}`
      });
    } catch (error) {
      newResults.push({
        test: 'Session Check (First Time)',
        duration: 0,
        status: 'error',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown'}`
      });
    }

    // Test 3: Session check again (should be cached)
    try {
      const start = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/session`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );
      const duration = Date.now() - start;
      newResults.push({
        test: 'Session Check (Cached)',
        duration,
        status: response.ok ? 'success' : 'error',
        details: response.ok 
          ? `Auth from cache in ${duration}ms (should be <100ms)` 
          : `Failed: ${response.status}`
      });
    } catch (error) {
      newResults.push({
        test: 'Session Check (Cached)',
        duration: 0,
        status: 'error',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown'}`
      });
    }

    // Test 4: Permissions endpoint
    try {
      const start = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/permissions`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );
      const duration = Date.now() - start;
      newResults.push({
        test: 'Permissions Endpoint',
        duration,
        status: response.ok ? 'success' : 'error',
        details: response.ok 
          ? `Loaded in ${duration}ms (should be <500ms with cache)` 
          : `Failed: ${response.status}`
      });
    } catch (error) {
      newResults.push({
        test: 'Permissions Endpoint',
        duration: 0,
        status: 'error',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown'}`
      });
    }

    setResults(newResults);
    setRunning(false);
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          <h3>Performance Diagnostics</h3>
        </div>
        <Button onClick={runDiagnostics} disabled={running}>
          {running ? 'Running Tests...' : 'Run Diagnostics'}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          {results.map((result, i) => (
            <div
              key={i}
              className={`p-3 rounded border ${
                result.status === 'success'
                  ? result.duration < 500
                    ? 'bg-green-50 border-green-200'
                    : 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{result.test}</span>
                <span className={`text-sm ${
                  result.status === 'success'
                    ? result.duration < 500
                      ? 'text-green-700'
                      : 'text-yellow-700'
                    : 'text-red-700'
                }`}>
                  {result.duration}ms
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{result.details}</p>
            </div>
          ))}

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm">
              <strong>Expected Performance:</strong>
            </p>
            <ul className="text-sm mt-2 space-y-1">
              <li>• Health Check: &lt;100ms</li>
              <li>• First Session Check: 50-10,000ms (depends on Supabase)</li>
              <li>• Cached Session Check: &lt;100ms (auth cache)</li>
              <li>• Permissions: &lt;500ms (with cache)</li>
            </ul>
            {results.some(r => r.duration > 2000) && (
              <p className="text-sm mt-3 text-red-700">
                <strong>Performance Issue Detected:</strong> Some requests took over 2 seconds. 
                Check Supabase Edge Function logs for details.
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}